import { ChangeEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { Image, Trash, XCircle } from 'react-feather';
import { StyleInjector } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { useSortable } from '@dnd-kit/react/sortable';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import config from '../../config';

import { getCookie } from '../helpers';
import { useNotifier } from '../Notifications/NotificationProvider';
import UI from '../UI';
import useProject from '../useProject';

interface UploadButtonProps {
  button: (props: { onClick: (event: MouseEvent<HTMLElement>) => void; }) => ReactNode;
  onSelect: (files: FileList) => void;
  maxSelectable?: number;
  accept?: string;
}

const UploadButton = ({ button, onSelect, maxSelectable = 1, accept }: UploadButtonProps) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    onSelect(files);

    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();

    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <>
      {button({ onClick: handleButtonClick })}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={maxSelectable > 1}
        disabled={maxSelectable < 1}
        accept={accept}
        onChange={handleFileUpload}
        aria-label={t('forms.upload')}
      />
    </>
  );
};

interface ImageInputProps {
  value: File | null;
  deleteImage: boolean;
  onChange: (file: File | null) => unknown;
  previewImage?: string | null;
  imageAlt?: string;
  width?: number;
  ratio?: number;
  accept?: string;
}

export const ImageInput = ({
  value, deleteImage, onChange, previewImage, imageAlt, width = 200, ratio, accept = '.jpg,.jpeg,.png,.webp',
}: ImageInputProps) => {
  const { t } = useTranslation('common');

  return (
    <UploadButton
      button={({ onClick }) => (
        <>
          {(value || (previewImage && !deleteImage)) && (
            <UI.Div sc={{ mb: 2 }}>
              <UI.Card
                sc={{
                  outline: 'gray.200',
                  borderRadius: 'md',
                  spacing: 'xs',
                }}
                style={{ width }}
              >
                <UI.Image sc={{ ratio }}>
                  <div>
                    <img
                      src={value ? URL.createObjectURL(value) : previewImage}
                      alt={imageAlt}
                      style={{ borderRadius: 3 }}
                    />
                  </div>
                </UI.Image>
                <UI.Div sc={{ textAlign: 'right', mt: 1 }}>
                  <UI.Button
                    onClick={() => onChange(null)}
                    sc={{ blank: true, size: 'sm' }}
                  >
                    <UI.Icon>
                      <XCircle />
                    </UI.Icon>
                    {' '}
                    {t('forms.remove')}
                  </UI.Button>
                </UI.Div>
              </UI.Card>
            </UI.Div>
          )}
          {(!value && (!previewImage || deleteImage)) && (
            <UI.Button onClick={onClick} sc={{ outline: true, mr: 1 }}>
              <UI.Icon>
                <Image />
              </UI.Icon>
              {' '}
              {t('forms.choose')}
            </UI.Button>
          )}
        </>
      )}
      onSelect={(files) => onChange(files[0])}
      accept={accept}
    />
  );
};

export const uploadImage = async (path: string, file: File): Promise<ImageFile> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(path, {
    method: 'POST',
    body: formData,
    headers: {
      'X-XSRF-TOKEN': decodeURIComponent(getCookie('XSRF-TOKEN')),
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  return response.json();
};

export interface ImageFile {
  id: string;
  url: string;
}

interface ImagesInputProps {
  value: ImageFile[];
  onChange: (files: ImageFile[]) => void;
  onUpload: (file: ImageFile) => void;
  max?: number;
  thumbnailSize?: number;
}

export const ImagesInput = ({ value, onChange, onUpload, max = 5, thumbnailSize = 150 }: ImagesInputProps) => {
  const [pending, setPending] = useState<number[]>([]);

  const deleteImage = (index: number) => {
    const changed = value.slice();
    changed.splice(index, 1);
    onChange(changed);
  };

  const handleUploadStart = (files: number) => {
    setPending((value) => [...value, ...Array.from({ length: files }, () => Math.random())]);
  };

  const handleUpload = (file: ImageFile) => {
    setPending((value) => value.slice(1));
    onUpload(file);
  };

  const handleError = () => {
    setPending((value) => value.slice(1));
  };

  const maxSelectable = max - value.length;

  return (
    <UI.InputGroup>
      <UI.FlexContainer sc={{ wrap: 'wrap', gap: 0.25 }}>
        <DragDropProvider
          onDragEnd={(event) => onChange(move(value, event))}
          plugins={(defaults) => [...defaults, StyleInjector.configure({ nonce: window.NONCE_ID })]}
        >
          {value.map((image, index) => (
            <UploadThumbnail
              image={image}
              index={index}
              onDelete={() => deleteImage(index)}
              thumbnailSize={thumbnailSize}
              key={image.id}
            />
          ))}
        </DragDropProvider>
        {pending.map((id) => (
          <UI.FlexContainer
            sc={{
              background: 'gray.100',
              borderRadius: 'md',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            style={{
              width: thumbnailSize,
              height: thumbnailSize,
            }}
            key={id}
          >
            <UI.Loader />
          </UI.FlexContainer>
        ))}
        {pending.length === 0 && maxSelectable > 0 && (
          <AsyncImageUploadButton
            onUpload={handleUpload}
            onError={handleError}
            onUploadStart={handleUploadStart}
            accept=".jpg,.jpeg,.png,.webp"
            maxSelectable={maxSelectable}
          />
        )}
      </UI.FlexContainer>
    </UI.InputGroup>
  );
};

interface UploadThumbnailProps {
  index: number;
  image: { id: string; url: string; };
  onDelete: () => unknown;
  thumbnailSize?: number;
}

const UploadThumbnail = ({ image, index, onDelete, thumbnailSize = 150 }: UploadThumbnailProps) => {
  const { t } = useTranslation('common');
  const { ref } = useSortable({ index, id: image.id });

  return (
    <UploadThumbnailContainer
      ref={ref}
      sc={{ background: 'gray.200', borderRadius: 'md', relative: true }}
      style={{ overflow: 'hidden', width: thumbnailSize, height: thumbnailSize, cursor: 'grab' }}
    >
      <img
        alt={image.id}
        src={image.url}
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
          height: '100%',
          display: 'block',
          position: 'relative',
          zIndex: 1,
        }}
      />
      <UI.FlexContainer
        sc={{
          absolute: true,
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'gray.900/70',
          padding: 1,
        }}
        style={{
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 2,
          backdropFilter: 'blur(3px)',
        }}
      >
        <UI.Icon sc={{ fontSize: 2.5, color: 'gray.100', ml: 1 }}>
          <UI.Icons.DragHandle />
        </UI.Icon>

        <UI.Button
          onClick={onDelete}
          sc={{ brand: 'gray.800', size: 'sm', blank: true }}
          aria-label={`${t('forms.remove')}: ${image.id}`}
        >
          <UI.Icon>
            <Trash />
          </UI.Icon>
        </UI.Button>
      </UI.FlexContainer>
    </UploadThumbnailContainer>
  );
};

const UploadThumbnailContainer = styled(UI.Div)`
  ${UI.FlexContainer} {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
  }

  &:hover, &[aria-grabbed=true], &[aria-hidden=true] {
    ${UI.FlexContainer} {
      opacity: 1;
    }
  }
`;

interface AsyncImageUploadButtonProps {
  onUploadStart?: (files: number) => void;
  onUpload: (file: ImageFile) => void;
  onError?: (file: File) => void;
  maxImageWidth?: number;
  maxImageHeight?: number;
  accept?: string;
  maxSelectable?: number;
  buttonSize?: number;
}

export const AsyncImageUploadButton = ({
  onUpload, onError = null, onUploadStart = null,
  maxImageWidth = 3000, maxImageHeight = 3000, accept = '.jpg,.jpeg,.png,.webp',
  maxSelectable = 1, buttonSize = 150,
}: AsyncImageUploadButtonProps) => {
  const { t } = useTranslation('common');
  const notifier = useNotifier();
  const project = useProject();

  const uploadPath = `${config.imageUploadPath}/${project.id}?max_width=${maxImageWidth || ''}&max_height=${maxImageHeight || ''}&temporary=true`;

  const onChange = (files: FileList) => {
    const max = Math.min(files.length, maxSelectable);
    onUploadStart?.(max);
    for (let i = 0; i < max; i++) {
      const filename = files[i].name;

      uploadImage(uploadPath, files[i])
        .then(onUpload)
        .catch(() => {
          notifier.error(t('editor.upload_failed', { filename }));
          onError?.(files[i]);
        });
    }
  };

  return (
    <UploadButton
      button={({ onClick }) => (
        <UI.NavCard
          onClick={onClick}
          sc={{
            borderRadius: 'md',
            outline: 'gray.100',
            background: 'gray.100',
          }}
          style={{ width: buttonSize, height: buttonSize }}
        >
          <UI.FlexContainer
            sc={{
              alignItems: 'center', justifyContent: 'center', direction: 'column',
            }}
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <UI.Icon sc={{ size: 2, color: 'gray.400' }}>
              <Image strokeWidth={1} />
            </UI.Icon>
            <UI.Div sc={{ mt: 1, textAlign: 'center' }}>
              {t('forms.select_images')}
            </UI.Div>
          </UI.FlexContainer>
        </UI.NavCard>
      )}
      onSelect={(files) => onChange(files)}
      accept={accept}
      maxSelectable={maxSelectable}
    />
  );
};

export default UploadButton;
